import { useEffect, useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Download, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { AttachedFileField } from '@/components/admin/AttachedFileField';
import { ExpiryBadge } from '@/components/admin/ExpiryBadge';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { useAuth } from '@/app/AuthProvider';
import {
  openAdminFile,
  useCompanyContracts,
  useDeleteCompanyContract,
  useSaveCompanyContract,
  type CompanyContract,
} from '@/features/admin/hooks';
import {
  CONTRACT_KINDS,
  CONTRACT_KIND_LABELS,
  CONTRACT_STATUSES,
  CONTRACT_STATUS_LABELS,
  friendlyAdminError,
} from '@/features/admin/model';
import { formatDateOnly } from '@/lib/formatters';
import { canWrite } from '@/lib/permissions';
import type { CompanyContractKind, CompanyContractStatus } from '@/types/database';

const STATUS_VARIANT: Record<CompanyContractStatus, 'muted' | 'success-soft' | 'warning'> = {
  draft: 'muted',
  active: 'success-soft',
  expired: 'warning',
  terminated: 'muted',
};

export function ContractsPage() {
  const { role } = useAuth();
  const canEdit = canWrite(role, 'company_admin');
  const { data, isLoading } = useCompanyContracts();
  const [editing, setEditing] = useState<CompanyContract | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<ColumnDef<CompanyContract>[]>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        header: 'Title',
        size: 240,
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.title}</span>
        ),
      },
      {
        id: 'counterparty',
        accessorFn: (row) => row.counterparty ?? '',
        header: 'Counterparty',
        size: 180,
        cell: ({ row }) =>
          row.original.counterparty || <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'kind',
        accessorFn: (row) => row.kind,
        header: 'Kind',
        size: 130,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Badge variant="secondary">{CONTRACT_KIND_LABELS[row.original.kind]}</Badge>
        ),
      },
      {
        id: 'status',
        accessorFn: (row) => row.status,
        header: 'Status',
        size: 120,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANT[row.original.status]}>
            {CONTRACT_STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
      {
        id: 'expires_on',
        accessorFn: (row) => row.expires_on ?? '',
        header: 'Ends',
        size: 150,
        cell: ({ row }) =>
          row.original.expires_on ? (
            <span className="flex items-center gap-2">
              {formatDateOnly(row.original.expires_on)}
              {row.original.auto_renews && <Badge variant="info-soft">Renews</Badge>}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'file',
        header: 'File',
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.storage_path ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                void openAdminFile(row.original.storage_path!).catch((err) =>
                  toast.error(friendlyAdminError(err, 'Could not open that file.')),
                );
              }}
            >
              <Download className="h-3.5 w-3.5" />
              Open
            </Button>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [],
  );

  const filters: FacetedFilter[] = useMemo(
    () => [
      {
        columnId: 'kind',
        title: 'Kind',
        options: CONTRACT_KINDS.map((kind) => ({ value: kind, label: CONTRACT_KIND_LABELS[kind] })),
      },
      {
        columnId: 'status',
        title: 'Status',
        options: CONTRACT_STATUSES.map((status) => ({
          value: status,
          label: CONTRACT_STATUS_LABELS[status],
        })),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Contracts"
        actions={
          canEdit ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add
            </Button>
          ) : undefined
        }
      />
      <Card>
        <CardContent className="pt-6">
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(row) => row.id}
            onRowClick={(row) => setEditing(row)}
            searchPlaceholder="Search contracts…"
            facetedFilters={filters}
            tableId="admin-contracts"
            initialPageSize={25}
            emptyState="No contracts on file yet."
            mobileCard={{
              title: (row) => row.title,
              subtitle: (row) => row.counterparty || '—',
              badges: (row) => [
                <Badge key="kind" variant="secondary">
                  {CONTRACT_KIND_LABELS[row.kind]}
                </Badge>,
                <Badge key="status" variant={STATUS_VARIANT[row.status]}>
                  {CONTRACT_STATUS_LABELS[row.status]}
                </Badge>,
              ],
            }}
          />
        </CardContent>
      </Card>
      <ContractDialog
        open={creating || !!editing}
        contract={editing}
        canEdit={canEdit}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

function ContractDialog({
  open,
  contract,
  canEdit,
  onOpenChange,
}: {
  open: boolean;
  contract: CompanyContract | null;
  canEdit: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const save = useSaveCompanyContract();
  const remove = useDeleteCompanyContract();
  const [title, setTitle] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [kind, setKind] = useState<CompanyContractKind>('vendor');
  const [status, setStatus] = useState<CompanyContractStatus>('active');
  const [effectiveOn, setEffectiveOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [autoRenews, setAutoRenews] = useState(false);
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(contract?.title ?? '');
    setCounterparty(contract?.counterparty ?? '');
    setKind(contract?.kind ?? 'vendor');
    setStatus(contract?.status ?? 'active');
    setEffectiveOn(contract?.effective_on ?? '');
    setExpiresOn(contract?.expires_on ?? '');
    setAutoRenews(contract?.auto_renews ?? false);
    setNotes(contract?.notes ?? '');
    setFile(null);
    setRemoveFile(false);
    setConfirmDelete(false);
  }, [open, contract]);

  const pending = save.isPending || remove.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!canEdit) return;
            void save
              .mutateAsync({
                id: contract?.id,
                title,
                counterparty,
                kind,
                status,
                effectiveOn,
                expiresOn,
                autoRenews,
                notes,
                file,
                removeFile,
                existingStoragePath: contract?.storage_path,
              })
              .then(() => {
                toast.success(contract ? 'Contract updated.' : 'Contract added.');
                onOpenChange(false);
              })
              .catch((err) => toast.error(friendlyAdminError(err, 'Could not save.')));
          }}
        >
          <DialogHeader>
            <DialogTitle>{contract ? contract.title : 'Add a contract'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="contract-title">Title</Label>
              <Input
                id="contract-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={!canEdit}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contract-party">Counterparty</Label>
              <Input
                id="contract-party"
                value={counterparty}
                onChange={(event) => setCounterparty(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Kind</Label>
              <Select
                value={kind}
                onValueChange={(value) => setKind(value as CompanyContractKind)}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRACT_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {CONTRACT_KIND_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(value) => setStatus(value as CompanyContractStatus)}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRACT_STATUSES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {CONTRACT_STATUS_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={autoRenews}
                  onCheckedChange={setAutoRenews}
                  disabled={!canEdit}
                  aria-label="Auto-renews"
                />
                Auto-renews
              </label>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contract-start">Effective</Label>
              <Input
                id="contract-start"
                type="date"
                value={effectiveOn}
                onChange={(event) => setEffectiveOn(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contract-end">Ends</Label>
              <Input
                id="contract-end"
                type="date"
                value={expiresOn}
                onChange={(event) => setExpiresOn(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            {expiresOn && (
              <div className="sm:col-span-2">
                <ExpiryBadge expiresOn={expiresOn} />
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="contract-notes">Notes</Label>
              <Textarea
                id="contract-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="sm:col-span-2">
              <AttachedFileField
                existingName={contract?.file_name ?? null}
                file={file}
                removeFile={removeFile}
                disabled={!canEdit}
                onFile={setFile}
                onRemoveFile={setRemoveFile}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {canEdit && contract ? (
              confirmDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={pending}
                  onClick={() =>
                    void remove
                      .mutateAsync({ id: contract.id, storagePath: contract.storage_path })
                      .then(() => {
                        toast.success('Contract deleted.');
                        onOpenChange(false);
                      })
                      .catch((err) => toast.error(friendlyAdminError(err, 'Could not delete.')))
                  }
                >
                  Confirm delete
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {canEdit && (
                <Button type="submit" disabled={pending || !title.trim()}>
                  Save
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
